import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDeleteAllowList } from 'src/features/parkingManagement/api/AllowListAPI/useDeleteAllowList';
import { LIST_ALLOW_LISTS_RESOURCE } from 'src/features/parkingManagement/api/AllowListAPI/useListAllowLists';
import { allowListMessages } from 'src/features/parkingManagement/utils/allowListMessages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogSeparator,
    DialogTitle,
    DialogHeader,
    DialogContent,
    DialogTrigger,
    DialogBody,
    DialogFooter,
    DialogClose,
    DialogDescription,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { AllowList } from 'src/features/parkingManagement/types/allowList';
import type { DataTableNewEntryProps } from 'src/registry/components/table';

interface AllowListFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedList: AllowList;
    children?: React.ReactNode;
}

export default function DeleteAllowListFormModal<TData>({
    selectedList,
    semantics,
    children,
}: AllowListFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { mutate, isPending } = useDeleteAllowList({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOW_LISTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(allowListMessages.deleteAllowListSuccess),
                description: formatMessage(allowListMessages.deleteAllowListSuccessDescription, {
                    entry: selectedList.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = React.useCallback(() => mutate({ listId: selectedList.id, parkingId }), [selectedList]);

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={allowListMessages.deleteAllowListSubtitle.id}
                                    defaultMessage={allowListMessages.deleteAllowListSubtitle.defaultMessage}
                                    values={{ entry: selectedList.name }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={allowListMessages.confirmDeleteAllowList.id}
                            defaultMessage={allowListMessages.confirmDeleteAllowList.defaultMessage}
                            values={{ entry: selectedList.name }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPending}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
